import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Cloud, 
  Key, 
  Download, 
  Copy, 
  Globe, 
  Calendar,
  LogOut,
  ShoppingCart,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Plus,
  CreditCard,
  Loader2,
  RefreshCw,
  Bell,
  AlertCircle,
  DollarSign,
  Settings,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Code2,
  Shield,
  Trash2,
  CheckCircle2,
  XCircle as XCircleIcon,
  Wifi
} from "lucide-react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const PRODUCTION_URL = "https://csyncpro.com";
const makeEmbedUrl = (token) => `${PRODUCTION_URL}/embed/${token}`;
const makeIframeCode = (token) => `<iframe src="${makeEmbedUrl(token)}" width="100%" height="800" frameborder="0" style="border:none;min-height:800px;"></iframe>`;
const makePreviewUrl = (token) => `${SITE_URL}/embed/${token}`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card Input Component
function PaymentMethodForm({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("auth_token");
      
      // Get setup intent from backend
      const setupResponse = await axios.post(
        `${API}/user/update-payment-method`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { client_secret } = setupResponse.data;

      // Confirm card setup with Stripe
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Save payment method to backend
      await axios.post(
        `${API}/user/save-payment-method`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { payment_method_id: setupIntent.payment_method }
        }
      );

      toast.success("Payment method saved successfully!");
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save payment method");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-slate-200 rounded-lg bg-white">
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1e293b',
                '::placeholder': { color: '#94a3b8' },
              },
              invalid: { color: '#ef4444' },
            },
          }}
        />
      </div>
      
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}
      
      <div className="flex gap-3">
        <Button 
          type="submit" 
          disabled={!stripe || loading}
          className="bg-bronze hover:bg-amber-600 text-white flex-1"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
          Save Card
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function UserDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [pluginVersion, setPluginVersion] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [emailForm, setEmailForm] = useState({ newEmail: "", password: "" });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsTab, setSettingsTab] = useState("email");
  const [transactions, setTransactions] = useState(null);
  const [showTransactions, setShowTransactions] = useState(false);
  const [embeds, setEmbeds] = useState([]);
  const [showEmbedCreate, setShowEmbedCreate] = useState(false);
  const [embedCreating, setEmbedCreating] = useState(false);
  const [embedForm, setEmbedForm] = useState({ license_key: "", title: "", description: "", primary_color: "#2563eb", accent_color: "#f59e0b" });
  const [embedCode, setEmbedCode] = useState(null);
  const [merchantCreds, setMerchantCreds] = useState([]);
  const [showMerchantSetup, setShowMerchantSetup] = useState(false);
  const [merchantForm, setMerchantForm] = useState({ processor: "fiserv", credentials: {} });
  const [merchantSaving, setMerchantSaving] = useState(false);
  const [showCloudbedsConnect, setShowCloudbedsConnect] = useState(null);
  const [cbForm, setCbForm] = useState({ api_key: "", property_id: "" });
  const [cbConnecting, setCbConnecting] = useState(false);
  const [cbTesting, setCbTesting] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    const storedUser = localStorage.getItem("user");
    
    if (!token || !storedUser) {
      navigate("/auth");
      return;
    }

    setUser(JSON.parse(storedUser));
    fetchData(token);
    fetchPluginVersion();
    fetchPaymentStatus(token);
    fetchTransactions(token);
    fetchEmbeds(token);
    fetchMerchantCreds(token);
  }, [navigate]);

  const fetchPaymentStatus = async (token) => {
    try {
      const response = await axios.get(`${API}/user/payment-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch payment status:", error);
    }
  };

  const fetchTransactions = async (token) => {
    try {
      const response = await axios.get(`${API}/user/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    }
  };

  const fetchEmbeds = async (token) => {
    try {
      const response = await axios.get(`${API}/user/embeds`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem("auth_token")}` }
      });
      setEmbeds(response.data.embeds || []);
    } catch (error) {
      console.error("Failed to fetch embeds:", error);
    }
  };

  const handleCreateEmbed = async () => {
    if (!embedForm.license_key) { toast.error("Select a license"); return; }
    setEmbedCreating(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(`${API}/user/embed/generate`, embedForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booking widget created!");
      setShowEmbedCreate(false);
      setEmbedCode({ embed_url: makeEmbedUrl(res.data.embed_token), iframe_code: makeIframeCode(res.data.embed_token) });
      fetchEmbeds(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create widget");
    }
    setEmbedCreating(false);
  };

  const fetchMerchantCreds = async (token) => {
    try {
      const res = await axios.get(`${API}/user/merchant/credentials`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem("auth_token")}` }
      });
      setMerchantCreds(res.data.credentials || []);
    } catch (e) { console.error("Failed to fetch merchant creds:", e); }
  };

  const handleSaveMerchantCreds = async () => {
    setMerchantSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(`${API}/user/merchant/credentials`, merchantForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${merchantForm.processor.charAt(0).toUpperCase() + merchantForm.processor.slice(1)} credentials saved!`);
      setShowMerchantSetup(false);
      fetchMerchantCreds(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save credentials");
    }
    setMerchantSaving(false);
  };

  const handleDeleteMerchantCreds = async (processor) => {
    if (!window.confirm(`Remove ${processor} credentials?`)) return;
    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(`${API}/user/merchant/credentials/${processor}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Credentials removed");
      fetchMerchantCreds(token);
    } catch { toast.error("Failed to remove credentials"); }
  };

  const handleConnectCloudbeds = async (licenseKey) => {
    if (!cbForm.api_key || !cbForm.property_id) { toast.error("Enter API Key and Property ID"); return; }
    setCbConnecting(true);
    try {
      await axios.post(`${API}/cloudbeds/connect-apikey`, {
        license_key: licenseKey,
        api_key: cbForm.api_key,
        property_id: cbForm.property_id
      });
      toast.success("Cloudbeds connected successfully!");
      setShowCloudbedsConnect(null);
      setCbForm({ api_key: "", property_id: "" });
      const token = localStorage.getItem("auth_token");
      fetchData(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to connect to Cloudbeds");
    }
    setCbConnecting(false);
  };

  const testCloudbedsConnection = async (licenseKey) => {
    setCbTesting(t => ({ ...t, [licenseKey]: "testing" }));
    try {
      const res = await axios.get(`${API}/cloudbeds/status/${licenseKey}`);
      if (res.data.connected) {
        setCbTesting(t => ({ ...t, [licenseKey]: "ok" }));
        toast.success(`Connected: ${res.data.hotel_name || "Cloudbeds"}`);
      } else {
        setCbTesting(t => ({ ...t, [licenseKey]: "fail" }));
        toast.error("Not connected");
      }
    } catch {
      setCbTesting(t => ({ ...t, [licenseKey]: "fail" }));
      toast.error("Connection test failed");
    }
    setTimeout(() => setCbTesting(t => ({ ...t, [licenseKey]: null })), 5000);
  };

  const fetchData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [licensesRes, purchasesRes] = await Promise.all([
        axios.get(`${API}/user/licenses`, { headers }),
        axios.get(`${API}/user/purchases`, { headers })
      ]);
      
      setLicenses(licensesRes.data.licenses);
      setPurchases(purchasesRes.data.purchases);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user");
        navigate("/auth");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPluginVersion = async () => {
    try {
      const response = await axios.get(`${API}/plugin/version`);
      setPluginVersion(response.data);
    } catch (error) {
      console.error("Failed to fetch plugin version:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const copyLicense = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("License key copied!");
  };

  const handlePurchase = async () => {
    if (!user?.email) return;
    
    setPurchaseLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        email: user.email,
        coupon_code: couponCode || null,
        origin_url: window.location.origin
      });
      
      // Redirect to checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checkout");
      setPurchaseLoading(false);
    }
  };

  const handlePayPending = async (purchase) => {
    // For pending purchases, create a new checkout session
    setPurchaseLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        email: user.email,
        coupon_code: null,
        origin_url: window.location.origin
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checkout");
      setPurchaseLoading(false);
    }
  };

  const downloadPlugin = (licenseKey) => {
    window.location.href = `${API}/download/plugin/${licenseKey}`;
  };

  const deactivateLicense = async (licenseKey) => {
    if (!confirm("Are you sure you want to deactivate this license from its current site? You can then activate it on a new site.")) {
      return;
    }

    const token = localStorage.getItem("auth_token");
    try {
      await axios.post(
        `${API}/user/license/${licenseKey}/deactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("License deactivated. You can now activate it on a new site.");
      fetchData(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate license");
    }
  };

  const handleUpdateEmail = async () => {
    if (!emailForm.newEmail || !emailForm.password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSettingsLoading(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const response = await axios.post(
        `${API}/user/update-email`,
        { new_email: emailForm.newEmail, password: emailForm.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local storage with new token and user info
      localStorage.setItem("auth_token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      setUser(response.data.user);
      
      toast.success("Email updated successfully!");
      setEmailForm({ newEmail: "", password: "" });
      setShowAccountSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update email");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setSettingsLoading(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      await axios.post(
        `${API}/user/update-password`,
        { current_password: passwordForm.currentPassword, new_password: passwordForm.newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Password updated successfully!");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setShowAccountSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setSettingsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { class: 'bg-emerald-100 text-emerald-700', icon: <CheckCircle className="w-3 h-3 mr-1" /> },
      pending: { class: 'bg-amber-100 text-amber-700', icon: <AlertTriangle className="w-3 h-3 mr-1" /> },
      revoked: { class: 'bg-red-100 text-red-700', icon: <XCircle className="w-3 h-3 mr-1" /> }
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge className={`${style.class} flex items-center`}>
        {style.icon}
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="CloudSync Pro" className="h-14 object-contain" />
          </Link>
          
          <div className="flex items-center gap-4">
            <span className="text-slate-600">{user?.email}</span>
            <Button 
              variant="ghost" 
              onClick={() => setShowAccountSettings(true)}
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Plugin Update Notification */}
        {pluginVersion && (
          <Card className="mb-6 border-amber-200 bg-amber-50" data-testid="update-notification">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-full">
                    <Bell className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">Plugin Update Available</h3>
                    <p className="text-sm text-amber-700">
                      Version <strong>{pluginVersion.version}</strong> is now available. 
                      {pluginVersion.changelog && ` - ${pluginVersion.changelog}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-amber-600 mr-2">
                    Can't auto-update? Download manually:
                  </p>
                  {licenses.filter(l => l.status === 'active').slice(0, 1).map(license => (
                    <Button 
                      key={license.license_key}
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => window.location.href = `${API}/download/plugin/${license.license_key}`}
                      data-testid="download-update-btn"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download v{pluginVersion.version}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-midnight font-outfit" data-testid="dashboard-title">
            My Dashboard
          </h1>
          <p className="text-slate-600">Manage your licenses and downloads</p>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-bronze/10 flex items-center justify-center">
                  <Key className="w-6 h-6 text-bronze" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Active Licenses</p>
                  <p className="text-2xl font-bold text-midnight">
                    {licenses.filter(l => l.status === 'active').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Purchases</p>
                  <p className="text-2xl font-bold text-midnight">
                    {purchases.filter(p => p.payment_status === 'paid').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Sites Activated</p>
                  <p className="text-2xl font-bold text-midnight">
                    {licenses.filter(l => l.woocommerce_url).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment Method Section - Critical for Weekly Billing */}
        <Card className={`mb-8 ${paymentStatus?.needs_payment_update ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} data-testid="payment-method-section">
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <CreditCard className={`w-5 h-5 ${paymentStatus?.needs_payment_update ? 'text-red-500' : 'text-bronze'}`} />
              Payment Method (2% Transaction Fee Billing)
              {paymentStatus?.needs_payment_update && (
                <Badge className="bg-red-100 text-red-700 ml-2">Action Required</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Your card is charged weekly for the 2% transaction fee on all bookings synced through CloudSync Pro.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {paymentStatus?.suspended_licenses?.length > 0 && (
              <div className="mb-4 p-4 bg-red-100 border border-red-300 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">License Suspended - Payment Required</p>
                    <p className="text-sm text-red-700 mt-1">
                      Your license has been suspended due to a billing issue. 
                      Please update your payment method to reactivate your license.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {paymentStatus?.total_outstanding > 0 && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-800">Outstanding Balance</p>
                      <p className="text-sm text-amber-700">
                        You have unpaid transaction fees
                      </p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-amber-700">${paymentStatus.total_outstanding.toFixed(2)}</p>
                </div>
              </div>
            )}

            {showPaymentForm ? (
              <Elements stripe={stripePromise}>
                <PaymentMethodForm 
                  onSuccess={() => {
                    setShowPaymentForm(false);
                    const token = localStorage.getItem("auth_token");
                    fetchPaymentStatus(token);
                    fetchData(token);
                  }}
                  onCancel={() => setShowPaymentForm(false)}
                />
              </Elements>
            ) : paymentStatus?.has_payment_method ? (
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">
                      {paymentStatus.card_brand?.toUpperCase()} ending in {paymentStatus.card_last_four}
                    </p>
                    <p className="text-sm text-slate-500">Your payment method is active</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPaymentForm(true)}
                  data-testid="update-card-btn"
                >
                  Update Card
                </Button>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-amber-600" />
                </div>
                <p className="text-slate-600 mb-4">
                  No payment method on file. Add a card to enable weekly billing for transaction fees.
                </p>
                <Button 
                  onClick={() => setShowPaymentForm(true)}
                  className="bg-bronze hover:bg-amber-600 text-white"
                  data-testid="add-card-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Payment Method
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Merchant Payment Processor Section */}
        <Card className="mb-8 border-slate-200" data-testid="merchant-credentials-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-outfit flex items-center gap-2">
                <Shield className="w-5 h-5 text-bronze" />
                Payment Processor
              </CardTitle>
              <CardDescription>
                Connect your merchant account to accept payments through your booking engine
              </CardDescription>
            </div>
            <Button
              onClick={() => { setMerchantForm({ processor: "fiserv", credentials: {} }); setShowMerchantSetup(true); }}
              className="bg-bronze hover:bg-amber-600 text-white"
              data-testid="setup-merchant-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Setup Processor
            </Button>
          </CardHeader>
          <CardContent>
            {merchantCreds.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No payment processor configured</p>
                <p className="text-sm text-slate-400 mt-1">Set up Fiserv, CyberSource, or Stripe to accept payments from your booking engine</p>
              </div>
            ) : (
              <div className="space-y-3">
                {merchantCreds.map(cred => (
                  <div key={cred.processor} className="border border-slate-200 rounded-lg p-4" data-testid={`merchant-cred-${cred.processor}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-sm capitalize">{cred.processor}</span>
                          <Badge className={cred.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{cred.status}</Badge>
                          <Badge variant="outline" className="text-xs">{cred.environment || "sandbox"}</Badge>
                        </div>
                        <div className="flex gap-4 text-xs text-slate-500 mt-1">
                          {cred.masked_credentials && Object.entries(cred.masked_credentials).map(([k, v]) => (
                            <span key={k}>{k.replace(/_/g, " ")}: <code className="bg-slate-100 px-1 rounded">{v}</code></span>
                          ))}
                        </div>
                        {cred.updated_at && <p className="text-xs text-slate-400 mt-1">Updated: {new Date(cred.updated_at).toLocaleDateString()}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setMerchantForm({ processor: cred.processor, credentials: {} }); setShowMerchantSetup(true); }} data-testid={`edit-merchant-${cred.processor}`}>
                          <Settings className="w-3.5 h-3.5 mr-1" /> Update
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteMerchantCreds(cred.processor)} data-testid={`delete-merchant-${cred.processor}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Transactions Section */}
        <Card className="mb-8 border-blue-200" data-testid="transactions-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-outfit flex items-center gap-2 text-slate-800">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                  My Transactions
                </CardTitle>
                <CardDescription>
                  View all bookings synced through CloudSync Pro and associated fees
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTransactions(!showTransactions)}
                data-testid="toggle-transactions-btn"
              >
                {showTransactions ? "Hide" : "View All"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Stats */}
            {transactions && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-800">{transactions.summary?.total_bookings || 0}</p>
                  <p className="text-xs text-slate-500">Total Bookings</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">${(transactions.summary?.total_booking_value || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Booking Value</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">${(transactions.summary?.total_fees || 0).toFixed(2)}</p>
                  <p className="text-xs text-slate-500">Platform Fees (2%)</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${transactions.summary?.total_outstanding > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className={`text-2xl font-bold ${transactions.summary?.total_outstanding > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    ${(transactions.summary?.total_outstanding || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500">Outstanding</p>
                </div>
              </div>
            )}

            {/* Transactions Table */}
            {showTransactions && transactions?.transactions && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-slate-600 font-medium">Guest</th>
                      <th className="text-left py-2 px-3 text-slate-600 font-medium">Check-in</th>
                      <th className="text-left py-2 px-3 text-slate-600 font-medium">Check-out</th>
                      <th className="text-right py-2 px-3 text-slate-600 font-medium">Amount</th>
                      <th className="text-right py-2 px-3 text-slate-600 font-medium">Fee (2%)</th>
                      <th className="text-center py-2 px-3 text-slate-600 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.transactions.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-slate-500">
                          No transactions yet. Bookings will appear here once synced.
                        </td>
                      </tr>
                    ) : (
                      transactions.transactions.map((tx, index) => (
                        <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium">{tx.guest_name || 'N/A'}</td>
                          <td className="py-2 px-3">{tx.checkin_date ? new Date(tx.checkin_date).toLocaleDateString() : '-'}</td>
                          <td className="py-2 px-3">{tx.checkout_date ? new Date(tx.checkout_date).toLocaleDateString() : '-'}</td>
                          <td className="py-2 px-3 text-right">${parseFloat(tx.booking_total || 0).toFixed(2)}</td>
                          <td className="py-2 px-3 text-right text-amber-700 font-medium">${parseFloat(tx.platform_fee_amount || 0).toFixed(2)}</td>
                          <td className="py-2 px-3 text-center">
                            <Badge variant={tx.fee_status === 'paid' ? 'default' : 'secondary'} className={tx.fee_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                              {tx.billing_status || 'pending'}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {!transactions && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cloudbeds Setup Instructions */}
        <Card className="mb-8 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50" data-testid="setup-instructions">
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2 text-green-800">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Easy Cloudbeds Setup with API Key
            </CardTitle>
            <CardDescription className="text-green-700">
              Connect to Cloudbeds in just 3 simple steps - no complex OAuth setup needed!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-white rounded-lg border border-green-200 p-4 mb-4">
              <ol className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-semibold text-xs">1</span>
                  <span>Go to <strong>Cloudbeds Dashboard</strong> → <strong>Marketplace</strong> → <strong>API Credentials</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-semibold text-xs">2</span>
                  <span>Click <strong>"+ NEW CREDENTIALS"</strong> → Select scopes (Hotel, Room, Reservation, Guest) → <strong>Create API Key</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-semibold text-xs">3</span>
                  <span>Copy the API Key (starts with <code className="bg-slate-100 px-1 rounded">cbat_</code>) → Paste in plugin settings → Done! ✓</span>
                </li>
              </ol>
            </div>
            
            <details className="group">
              <summary className="cursor-pointer text-sm font-semibold text-green-700 hover:text-green-800 flex items-center gap-2">
                <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
                View detailed instructions
              </summary>
              <ol className="mt-4 ml-6 space-y-2 text-sm text-slate-600 list-decimal">
                <li><strong>Download & Install</strong> the plugin from this dashboard</li>
                <li>Log in to your <a href="https://hotels.cloudbeds.com" target="_blank" rel="noopener noreferrer" className="text-green-600 underline hover:text-green-800">Cloudbeds Dashboard</a></li>
                <li>Go to <strong>Marketplace → API Credentials</strong></li>
                <li>Click <strong>"+ NEW CREDENTIALS"</strong></li>
                <li>Select scopes: <strong>Hotel (Read), Room (Read), Reservation (Read/Write), Guest (Read/Write)</strong></li>
                <li>Click <strong>"CREATE"</strong> and copy your API Key</li>
                <li>In WordPress, go to <strong>CloudSync Pro → Settings</strong></li>
                <li>Paste your API Key and click <strong>"Connect to Cloudbeds"</strong></li>
                <li>That's it! Your rooms will sync automatically 🎉</li>
              </ol>
            </details>
          </CardContent>
        </Card>

        {/* Licenses Section */}
        <Card className="mb-8" data-testid="licenses-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-outfit flex items-center gap-2">
                <Key className="w-5 h-5 text-bronze" />
                My Licenses
              </CardTitle>
              <CardDescription>
                Each license can only be activated on ONE WooCommerce site at a time
              </CardDescription>
            </div>
            <Button 
              onClick={() => setShowPurchaseDialog(true)}
              className="bg-bronze hover:bg-amber-600 text-white"
              data-testid="add-license-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add License
            </Button>
          </CardHeader>
          <CardContent>
            {licenses.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Key className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No licenses yet</p>
                <Link to="/">
                  <Button className="mt-4 bg-bronze hover:bg-amber-600 text-white">
                    Purchase CloudSync Pro
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {licenses.map((license, index) => (
                  <div 
                    key={license.id || index}
                    className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
                    data-testid={`license-card-${index}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <code className="text-lg font-bold text-midnight font-mono bg-slate-100 px-3 py-1 rounded">
                            {license.license_key}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyLicense(license.license_key)}
                            data-testid={`copy-license-${index}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {getStatusBadge(license.status)}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>Created: {formatDate(license.created_at)}</span>
                          </div>
                          
                          {license.woocommerce_url ? (
                            <div className="flex items-center gap-1 text-emerald-600">
                              <Globe className="w-4 h-4" />
                              <span>Active on: {license.woocommerce_url}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-amber-600">
                              <AlertTriangle className="w-4 h-4" />
                              <span>Not activated on any site yet</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Cloudbeds Connection Status */}
                        {license.cloudbeds_connected ? (
                          <div className="flex items-center gap-1.5 text-xs">
                            <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Cloudbeds Connected
                            </Badge>
                            <Button variant="ghost" size="sm" className="h-7 text-xs"
                              onClick={() => testCloudbedsConnection(license.license_key)}
                              data-testid={`test-cb-${index}`}>
                              {cbTesting[license.license_key] === "testing" ? <Loader2 className="w-3 h-3 animate-spin" /> :
                               cbTesting[license.license_key] === "ok" ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> :
                               cbTesting[license.license_key] === "fail" ? <XCircleIcon className="w-3 h-3 text-red-500" /> :
                               <Wifi className="w-3 h-3" />}
                              Test
                            </Button>
                          </div>
                        ) : (
                          <Button variant="outline" size="sm" className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => { setShowCloudbedsConnect(license.license_key); setCbForm({ api_key: "", property_id: "" }); }}
                            data-testid={`connect-cb-${index}`}>
                            <Cloud className="w-3.5 h-3.5 mr-1" /> Connect Cloudbeds
                          </Button>
                        )}

                        {license.status === 'active' && (
                          <>
                            <Button 
                              variant="outline"
                              onClick={() => downloadPlugin(license.license_key)}
                              data-testid={`download-plugin-${index}`}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download Plugin
                            </Button>
                            
                            {license.woocommerce_url && (
                              <Button 
                                variant="ghost"
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => deactivateLicense(license.license_key)}
                                data-testid={`deactivate-license-${index}`}
                              >
                                Move to New Site
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Embed Widgets Section */}
        <Card className="mb-8" data-testid="embeds-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-outfit flex items-center gap-2">
                <Code2 className="w-5 h-5 text-bronze" />
                Booking Widgets
              </CardTitle>
              <CardDescription>
                Embeddable booking forms you can put on any website
              </CardDescription>
            </div>
            {licenses.filter(l => l.cloudbeds_connected).length > 0 && (
              <Button
                onClick={() => setShowEmbedCreate(true)}
                className="bg-bronze hover:bg-amber-600 text-white"
                data-testid="create-embed-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Widget
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {embeds.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Code2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No booking widgets yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  {licenses.filter(l => l.cloudbeds_connected).length > 0
                    ? "Create a widget to embed a booking form on any website"
                    : "Connect Cloudbeds to a license first, then create a booking widget"}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {embeds.map((embed, index) => (
                  <div key={embed.embed_token} className="border border-slate-200 rounded-lg p-4" data-testid={`embed-item-${index}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{embed.property_name}</span>
                          <Badge variant={embed.status === "active" ? "default" : "secondary"} className="text-xs">{embed.status}</Badge>
                        </div>
                        <p className="text-xs text-slate-400">{embed.bookings_count || 0} bookings</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEmbedCode({
                            embed_url: makeEmbedUrl(embed.embed_token),
                            iframe_code: makeIframeCode(embed.embed_token)
                          })}
                          data-testid={`embed-code-${index}`}
                        >
                          <Copy className="w-3.5 h-3.5 mr-1" /> Get Code
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => window.open(makePreviewUrl(embed.embed_token), "_blank")} data-testid={`embed-preview-${index}`}>
                          <Globe className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Purchase History */}
        <Card data-testid="purchases-section">
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-bronze" />
              Purchase History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {purchases.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <p>No purchases yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">License Key</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Amount</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((purchase, index) => (
                      <tr 
                        key={purchase.id || index}
                        className="border-b border-slate-100"
                        data-testid={`purchase-row-${index}`}
                      >
                        <td className="py-3 px-4">{formatDate(purchase.created_at)}</td>
                        <td className="py-3 px-4">
                          <code className="text-sm bg-slate-100 px-2 py-1 rounded font-mono">
                            {purchase.license_key}
                          </code>
                        </td>
                        <td className="py-3 px-4 font-semibold text-emerald-600">
                          ${purchase.amount?.toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={purchase.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                            {purchase.payment_status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {purchase.payment_status === 'pending' && (
                            <Button 
                              size="sm"
                              onClick={() => handlePayPending(purchase)}
                              className="bg-bronze hover:bg-amber-600 text-white"
                              data-testid={`pay-now-${index}`}
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay Now
                            </Button>
                          )}
                          {purchase.payment_status === 'paid' && (
                            <span className="text-emerald-600 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Completed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="mt-8 bg-slate-800 text-white">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-2">Need Help?</h3>
            <p className="text-slate-300 mb-4">
              Each license is valid for ONE WooCommerce site. After installing the plugin, it will automatically register with your license key.
              If you need to move to a different site, click "Move to New Site" to deactivate the current installation first.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-bronze hover:underline">Documentation</a>
              <a href="#" className="text-bronze hover:underline">Contact Support</a>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Purchase Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Purchase Additional License</DialogTitle>
            <DialogDescription>
              Each license allows you to use CloudSync Pro on one WooCommerce site.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">License Price</p>
              <p className="text-3xl font-bold text-midnight">$289.00</p>
              <p className="text-xs text-slate-500">One-time payment + 2% transaction fee</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="coupon">Coupon Code (optional)</Label>
              <Input 
                id="coupon"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                data-testid="purchase-coupon-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowPurchaseDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePurchase}
              disabled={purchaseLoading}
              className="bg-bronze hover:bg-amber-600 text-white"
              data-testid="confirm-purchase-btn"
            >
              {purchaseLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Proceed to Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account Settings Dialog */}
      <Dialog open={showAccountSettings} onOpenChange={setShowAccountSettings}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Settings className="w-5 h-5 text-bronze" />
              Account Settings
            </DialogTitle>
            <DialogDescription>
              Update your email address or password
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Tab Buttons */}
            <div className="flex gap-2 border-b border-slate-200 pb-2">
              <Button
                variant={settingsTab === "email" ? "default" : "ghost"}
                size="sm"
                onClick={() => setSettingsTab("email")}
                className={settingsTab === "email" ? "bg-bronze hover:bg-amber-600 text-white" : ""}
              >
                <Mail className="w-4 h-4 mr-2" />
                Change Email
              </Button>
              <Button
                variant={settingsTab === "password" ? "default" : "ghost"}
                size="sm"
                onClick={() => setSettingsTab("password")}
                className={settingsTab === "password" ? "bg-bronze hover:bg-amber-600 text-white" : ""}
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </div>

            {/* Change Email Form */}
            {settingsTab === "email" && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Current email:</p>
                  <p className="font-medium text-midnight">{user?.email}</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newEmail">New Email Address</Label>
                  <Input
                    id="newEmail"
                    type="email"
                    placeholder="Enter new email"
                    value={emailForm.newEmail}
                    onChange={(e) => setEmailForm({...emailForm, newEmail: e.target.value})}
                    data-testid="new-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="emailPassword">Current Password (for verification)</Label>
                  <div className="relative">
                    <Input
                      id="emailPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={emailForm.password}
                      onChange={(e) => setEmailForm({...emailForm, password: e.target.value})}
                      data-testid="email-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <Button
                  onClick={handleUpdateEmail}
                  disabled={settingsLoading}
                  className="w-full bg-bronze hover:bg-amber-600 text-white"
                  data-testid="update-email-btn"
                >
                  {settingsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Update Email
                </Button>
              </div>
            )}

            {/* Change Password Form */}
            {settingsTab === "password" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Enter current password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      data-testid="current-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password (min 8 characters)"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      data-testid="new-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <Button
                  onClick={handleUpdatePassword}
                  disabled={settingsLoading}
                  className="w-full bg-bronze hover:bg-amber-600 text-white"
                  data-testid="update-password-btn"
                >
                  {settingsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Update Password
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Embed Dialog */}
      <Dialog open={showEmbedCreate} onOpenChange={setShowEmbedCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Code2 className="w-5 h-5 text-bronze" />
              Create Booking Widget
            </DialogTitle>
            <DialogDescription>
              Generate an embeddable booking form for any website
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm">Property *</Label>
              <select
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={embedForm.license_key}
                onChange={e => setEmbedForm(f => ({ ...f, license_key: e.target.value }))}
                data-testid="embed-license-select"
              >
                <option value="">Select a license...</option>
                {licenses.filter(l => l.cloudbeds_connected).map(l => (
                  <option key={l.license_key} value={l.license_key}>
                    {l.cloudbeds_hotel_name || l.license_key}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm">Widget Title</Label>
              <Input
                value={embedForm.title}
                onChange={e => setEmbedForm(f => ({ ...f, title: e.target.value }))}
                placeholder="My Hotel Bookings"
                className="mt-1"
                data-testid="embed-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Primary Color</Label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={embedForm.primary_color} onChange={e => setEmbedForm(f => ({ ...f, primary_color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border" />
                  <Input value={embedForm.primary_color} onChange={e => setEmbedForm(f => ({ ...f, primary_color: e.target.value }))} className="flex-1" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Accent Color</Label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={embedForm.accent_color} onChange={e => setEmbedForm(f => ({ ...f, accent_color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border" />
                  <Input value={embedForm.accent_color} onChange={e => setEmbedForm(f => ({ ...f, accent_color: e.target.value }))} className="flex-1" />
                </div>
              </div>
            </div>
            <Button onClick={handleCreateEmbed} disabled={embedCreating || !embedForm.license_key} className="w-full bg-bronze hover:bg-amber-600 text-white" data-testid="submit-embed-btn">
              {embedCreating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Generate Widget
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Embed Code Dialog */}
      <Dialog open={!!embedCode} onOpenChange={() => setEmbedCode(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Code2 className="w-5 h-5 text-bronze" />
              Your Embed Code
            </DialogTitle>
          </DialogHeader>
          {embedCode && (
            <div className="space-y-4 py-2">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-semibold">iFrame Code</Label>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(embedCode.iframe_code); toast.success("Copied!"); }} data-testid="copy-embed-iframe">
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                  </Button>
                </div>
                <pre className="bg-slate-900 text-green-400 rounded-lg p-3 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                  {embedCode.iframe_code}
                </pre>
              </div>
              <div>
                <Label className="text-sm font-semibold">Direct Link</Label>
                <div className="bg-slate-100 rounded-lg p-3 mt-1">
                  <a href={embedCode.embed_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm break-all">{embedCode.embed_url}</a>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                Paste the iFrame code into any HTML page. Works on WordPress, Squarespace, Wix, or any website. Bookings sync to Cloudbeds in real-time.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Merchant Credentials Setup Dialog */}
      <Dialog open={showMerchantSetup} onOpenChange={setShowMerchantSetup}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Shield className="w-5 h-5 text-bronze" />
              Payment Processor Setup
            </DialogTitle>
            <DialogDescription>
              Enter your merchant credentials to accept payments through your booking engine
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold">Processor</Label>
              <select
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={merchantForm.processor}
                onChange={e => setMerchantForm({ processor: e.target.value, credentials: {} })}
                data-testid="merchant-processor-select"
              >
                <option value="fiserv">Fiserv (Scotiabank)</option>
                <option value="cybersource">CyberSource (Visa)</option>
                <option value="stripe">Stripe</option>
              </select>
            </div>

            {merchantForm.processor === "fiserv" && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Store ID (Store Name) *</Label>
                  <Input placeholder="Your Fiserv IPG Store ID" className="mt-1" value={merchantForm.credentials.store_id || ""}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, store_id: e.target.value } }))}
                    data-testid="fiserv-store-id" />
                </div>
                <div>
                  <Label className="text-sm">Shared Secret (Shared Key) *</Label>
                  <Input placeholder="Shared Secret" type="password" className="mt-1" value={merchantForm.credentials.shared_secret || ""}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, shared_secret: e.target.value } }))}
                    data-testid="fiserv-shared-secret" />
                </div>
                <div>
                  <Label className="text-sm">Currency *</Label>
                  <select className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={merchantForm.credentials.currency || "USD"}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, currency: e.target.value } }))}
                    data-testid="fiserv-currency">
                    <optgroup label="Caribbean">
                      <option value="BSD">BSD — Bahamian Dollar</option>
                      <option value="BBD">BBD — Barbadian Dollar</option>
                      <option value="JMD">JMD — Jamaican Dollar</option>
                      <option value="TTD">TTD — Trinidad & Tobago Dollar</option>
                      <option value="XCD">XCD — East Caribbean Dollar</option>
                      <option value="DOP">DOP — Dominican Peso</option>
                      <option value="HTG">HTG — Haitian Gourde</option>
                      <option value="AWG">AWG — Aruban Florin</option>
                      <option value="ANG">ANG — Neth. Antillean Guilder</option>
                      <option value="KYD">KYD — Cayman Islands Dollar</option>
                      <option value="BZD">BZD — Belize Dollar</option>
                      <option value="GYD">GYD — Guyanese Dollar</option>
                      <option value="SRD">SRD — Surinamese Dollar</option>
                    </optgroup>
                    <optgroup label="International">
                      <option value="USD">USD — US Dollar</option>
                      <option value="EUR">EUR — Euro</option>
                      <option value="GBP">GBP — British Pound</option>
                      <option value="CAD">CAD — Canadian Dollar</option>
                      <option value="AUD">AUD — Australian Dollar</option>
                      <option value="JPY">JPY — Japanese Yen</option>
                      <option value="CHF">CHF — Swiss Franc</option>
                      <option value="CNY">CNY — Chinese Yuan</option>
                      <option value="HKD">HKD — Hong Kong Dollar</option>
                      <option value="SGD">SGD — Singapore Dollar</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Environment</Label>
                  <select className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={merchantForm.credentials.environment || "sandbox"}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, environment: e.target.value } }))}
                    data-testid="fiserv-environment">
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>
              </div>
            )}

            {merchantForm.processor === "cybersource" && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Profile ID *</Label>
                  <Input placeholder="CyberSource Secure Acceptance Profile ID" className="mt-1" value={merchantForm.credentials.profile_id || ""}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, profile_id: e.target.value } }))}
                    data-testid="cyber-profile-id" />
                  <p className="text-xs text-slate-400 mt-1">Business Center → Payment Configuration → Secure Acceptance Settings → Profile</p>
                </div>
                <div>
                  <Label className="text-sm">Access Key *</Label>
                  <Input placeholder="Access Key from Security tab" type="password" className="mt-1" value={merchantForm.credentials.access_key || ""}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, access_key: e.target.value } }))}
                    data-testid="cyber-access-key" />
                </div>
                <div>
                  <Label className="text-sm">Secret Key *</Label>
                  <Input placeholder="Secret Key from Security tab" type="password" className="mt-1" value={merchantForm.credentials.secret_key || ""}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, secret_key: e.target.value } }))}
                    data-testid="cyber-secret-key" />
                </div>
                <div>
                  <Label className="text-sm">Currency *</Label>
                  <select className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={merchantForm.credentials.currency || "USD"}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, currency: e.target.value } }))}
                    data-testid="cyber-currency">
                    <optgroup label="Caribbean">
                      <option value="BSD">BSD — Bahamian Dollar</option>
                      <option value="BBD">BBD — Barbadian Dollar</option>
                      <option value="JMD">JMD — Jamaican Dollar</option>
                      <option value="TTD">TTD — Trinidad & Tobago Dollar</option>
                      <option value="XCD">XCD — East Caribbean Dollar</option>
                      <option value="DOP">DOP — Dominican Peso</option>
                      <option value="HTG">HTG — Haitian Gourde</option>
                      <option value="AWG">AWG — Aruban Florin</option>
                      <option value="ANG">ANG — Neth. Antillean Guilder</option>
                      <option value="KYD">KYD — Cayman Islands Dollar</option>
                      <option value="BZD">BZD — Belize Dollar</option>
                      <option value="GYD">GYD — Guyanese Dollar</option>
                      <option value="SRD">SRD — Surinamese Dollar</option>
                    </optgroup>
                    <optgroup label="International">
                      <option value="USD">USD — US Dollar</option>
                      <option value="EUR">EUR — Euro</option>
                      <option value="GBP">GBP — British Pound</option>
                      <option value="CAD">CAD — Canadian Dollar</option>
                      <option value="AUD">AUD — Australian Dollar</option>
                      <option value="JPY">JPY — Japanese Yen</option>
                      <option value="CHF">CHF — Swiss Franc</option>
                      <option value="CNY">CNY — Chinese Yuan</option>
                      <option value="HKD">HKD — Hong Kong Dollar</option>
                      <option value="SGD">SGD — Singapore Dollar</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Environment</Label>
                  <select className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={merchantForm.credentials.environment || "sandbox"}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, environment: e.target.value } }))}
                    data-testid="cyber-environment">
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>
              </div>
            )}

            {merchantForm.processor === "stripe" && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Stripe Account ID *</Label>
                  <Input placeholder="acct_..." className="mt-1" value={merchantForm.credentials.stripe_account_id || ""}
                    onChange={e => setMerchantForm(f => ({ ...f, credentials: { ...f.credentials, stripe_account_id: e.target.value } }))}
                    data-testid="stripe-account-id" />
                </div>
              </div>
            )}

            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
              <p className="font-medium text-slate-700 mb-1">Security</p>
              <p>Credentials are encrypted and stored securely. Only masked versions are displayed after saving. Your credentials are used solely to process payments through your booking engine.</p>
            </div>

            <Button onClick={handleSaveMerchantCreds} disabled={merchantSaving} className="w-full bg-bronze hover:bg-amber-600 text-white" data-testid="save-merchant-btn">
              {merchantSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Save Credentials
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cloudbeds Connect Dialog */}
      <Dialog open={!!showCloudbedsConnect} onOpenChange={() => setShowCloudbedsConnect(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Cloud className="w-5 h-5 text-bronze" />
              Connect to Cloudbeds
            </DialogTitle>
            <DialogDescription>
              Enter your Cloudbeds API credentials to sync rooms and accept bookings
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold">Cloudbeds API Key *</Label>
              <Input
                value={cbForm.api_key}
                onChange={e => setCbForm(f => ({ ...f, api_key: e.target.value }))}
                placeholder="cbat_xxxxxxxxxxxxxxxxx"
                type="password"
                className="mt-1 font-mono"
                data-testid="cb-api-key-input"
              />
              <p className="text-xs text-slate-400 mt-1">Find this in Cloudbeds under Settings &rarr; API Credentials</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Property ID *</Label>
              <Input
                value={cbForm.property_id}
                onChange={e => setCbForm(f => ({ ...f, property_id: e.target.value }))}
                placeholder="e.g. 86266559906004"
                className="mt-1 font-mono"
                data-testid="cb-property-id-input"
              />
              <p className="text-xs text-slate-400 mt-1">Your Cloudbeds Property ID (found in your Cloudbeds dashboard URL)</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
              <p className="font-medium text-slate-700 mb-1">What happens when you connect?</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>We verify your API key against Cloudbeds</li>
                <li>Room types and photos are synced automatically</li>
                <li>You can then create a booking widget for your website</li>
                <li>All bookings sync directly to your Cloudbeds PMS</li>
              </ul>
            </div>

            <Button
              onClick={() => handleConnectCloudbeds(showCloudbedsConnect)}
              disabled={cbConnecting || !cbForm.api_key || !cbForm.property_id}
              className="w-full bg-bronze hover:bg-amber-600 text-white"
              data-testid="connect-cb-submit"
            >
              {cbConnecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Cloud className="w-4 h-4 mr-2" />}
              Connect & Verify
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
